import { useState } from 'react'

import { Box, Button, Spinner, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { currencyToNumber } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import SummaryTotal from 'modules/payroll_bundle/components/SummaryTotal'
import useFetchSupplementInsights, {
  totalByCurrencyForPreviousPeriod,
  totalByTaxonomyForPreviousPeriod,
} from 'modules/payroll_bundle/hooks/useFetchSupplementInsights'
import { useSupplementsInsights } from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'

import SupplementsInsightsSidePanel from './SupplementsInsightsSidePanel'

const MAX_TAXONOMIES_SHOWN = 3

interface Params {
  supplementsStore: SupplementsFormStore
}

export function isSupplementFormInvalid(form: SupplementForm): boolean {
  const hasTaxonomyId = form.fields.contracts_taxonomy_id?.hasValue

  if (!hasTaxonomyId) {
    return true
  }

  if (!form.isValid) {
    return true
  }

  return false
}

const SupplementsInsights = ({ supplementsStore }: Params) => {
  const [showSidePanel, setShowSidePanel] = useState(false)

  const [totalPendingSupplements, setTotalPendingSupplements] = useState(0)
  const [numberOfTaxonomyToShow, setNumberOfTaxonomyToShow] = useState(0)

  const { loading: isLoading, fetchInsightsData } = useFetchSupplementInsights({
    triggerFetch: true,
  })

  const {
    legalEntityCount,
    totalByTaxonomy,
    previousPeriodTotalByTaxonomy,
    totalByCurrency,
    currentPeriodSupplementCount,
    currentPeriodDraftSupplementCount,
    previousPeriodTotalByCurrency,
  } = useSupplementsInsights()

  const totalByCurrencyNonZero = totalByCurrency.filter((currency) => {
    const total = Number(currency.total)
    return total !== 0 && !isNaN(total)
  })
  const totalByTaxonomyNonZero = totalByTaxonomy.filter((taxonomy) => {
    const total = Number(taxonomy.total)
    return total !== 0 && !isNaN(total)
  })

  // Why is totalByCurrency an array?
  const totalByCurrencyAmount = totalByCurrencyNonZero[0]?.total ?? 0

  const centValuesCombined = supplementsStore.forms.reduce((acc, form) => {
    const amountInCents = form.get('amount_in_cents').value
    return acc + '-' + amountInCents.toString()
  }, '')

  const openSidePanel = () => setShowSidePanel(true)

  const closeSidePanel = () => setShowSidePanel(false)

  useUnsafeEffect(() => {
    fetchInsightsData().then(() => {
      const pendingSupplementForms = supplementsStore.forms.filter((form) => {
        return isSupplementFormInvalid(form)
      })

      const _totalPendingSupplements = pendingSupplementForms.length

      const _numberOfTaxonomyToShow =
        totalByCurrency.length < MAX_TAXONOMIES_SHOWN
          ? MAX_TAXONOMIES_SHOWN - totalByCurrency.length
          : 0

      setTotalPendingSupplements(_totalPendingSupplements)
      setNumberOfTaxonomyToShow(_numberOfTaxonomyToShow)
    })
  }, [
    fetchInsightsData,
    supplementsStore.forms.length,
    totalByCurrency.length,
    totalByCurrencyAmount,
    currentPeriodSupplementCount,
    currentPeriodDraftSupplementCount,
    supplementsStore,
    centValuesCombined,
  ])

  if (currentPeriodSupplementCount === 0) return null

  if (isLoading) {
    return (
      <Box width="full" alignItems="center" paddingTop="s64" height="s120">
        <Spinner />
      </Box>
    )
  }

  return (
    <>
      <Box
        flexDirection="row"
        borderRadius={{ all: 'abs008' }}
        border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
        paddingX="s24"
        paddingY="s12"
        gap="s24"
      >
        <Box
          justifyContent="center"
          gap="s12"
          border={{ right: { color: 'grey400', width: 's1', style: 'solid' } }}
          paddingRight="s12"
          width="s300"
        >
          <Box>
            <Text.Normal weight="semibold">
              {i18n.t('payroll_bundle.insights_from_supplements.supplemends_to_be_filled')}
            </Text.Normal>
          </Box>
          <Box alignSelf="flexStart">
            <Tag type={totalPendingSupplements === 0 ? 'correct' : 'champagne'}>
              {totalPendingSupplements}
            </Tag>
          </Box>
        </Box>

        {totalByCurrencyNonZero.map((el) => {
          const previousPeriodTotal = totalByCurrencyForPreviousPeriod(
            previousPeriodTotalByCurrency,
            el.currency
          )

          return (
            <SummaryTotal
              key={el.currency}
              title={i18n.t('payroll_bundle.insights_from_supplements.total')}
              totalCurrentPeriodInCents={currencyToNumber(el.total)}
              totalPreviousPeriodInCents={previousPeriodTotal > 0 ? previousPeriodTotal : undefined}
              currency={el.currency}
            />
          )
        })}

        {totalByTaxonomyNonZero.slice(0, numberOfTaxonomyToShow).map((el) => {
          return (
            <SummaryTotal
              key={el.taxonomyId}
              title={el.taxonomyName}
              subtitle={legalEntityCount > 1 ? el.legalEntityName : ''}
              totalCurrentPeriodInCents={currencyToNumber(el.total)}
              totalPreviousPeriodInCents={
                totalByTaxonomyForPreviousPeriod(previousPeriodTotalByTaxonomy, el.taxonomyId)
                  ? currencyToNumber(
                      totalByTaxonomyForPreviousPeriod(previousPeriodTotalByTaxonomy, el.taxonomyId)
                    )
                  : undefined
              }
              currency={el.legalEntityCurrency}
            />
          )
        })}

        {totalByTaxonomyNonZero.length > 0 && (
          <Box justifyContent="center">
            <Button type="secondary" onClick={openSidePanel} iconName="arrow-right" />
          </Box>
        )}
      </Box>

      <SupplementsInsightsSidePanel
        totalByTaxonomy={totalByTaxonomy}
        previousTotalByTaxonomy={previousPeriodTotalByTaxonomy}
        totalByCurrency={totalByCurrency}
        previousTotalByCurrency={previousPeriodTotalByCurrency}
        legalEntityCount={legalEntityCount}
        open={showSidePanel}
        onClose={closeSidePanel}
      />
    </>
  )
}

export default observer(SupplementsInsights)
